/**
 * Auth MCP Tools
 *
 * Implements stackwright_pro_configure_auth — the tool that the auth-otter
 * has referenced since day one but that was never built. Its absence meant the
 * auth-otter would fall back to writing middleware.ts directly, bypassing the
 * TOOL GUARD that was supposed to prevent exactly that.
 *
 * For CAC/PKI: generated middleware.ts carries an explicit SECURITY REVIEW
 * REQUIRED comment. Certificate chain validation must be verified by a
 * security officer before production deployment on DoD systems.
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { readFileSync, writeFileSync, existsSync, mkdirSync } from 'fs';
import { join } from 'path';
import { jsonCoerce, boolCoerce, numCoerce } from '../coerce.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConfigureAuthParams {
  method: 'cac' | 'oidc' | 'oauth2' | 'none';
  provider?: 'azure-ad' | 'okta' | 'ping' | 'cognito' | 'custom';
  // CAC
  cacCaBundle?: string;
  cacEdipiLookup?: string;
  cacOcspEndpoint?: string;
  cacCertHeader?: string;
  // OIDC
  oidcDiscoveryUrl?: string;
  oidcClientId?: string;
  oidcClientSecret?: string;
  oidcScopes?: string;
  oidcRoleClaim?: string;
  // OAuth2
  oauth2AuthUrl?: string;
  oauth2TokenUrl?: string;
  oauth2ClientId?: string;
  oauth2ClientSecret?: string;
  oauth2Scopes?: string;
  // RBAC
  rbacRoles?: string[];
  rbacDefaultRole?: string;
  // Audit
  auditEnabled?: boolean;
  auditRetentionDays?: number;
  // Routes — accepts bare strings (backward-compat, fall back to defaultRole)
  // or per-route objects with an explicit requiredRole (swp-owu0)
  protectedRoutes?: Array<string | { pattern: string; requiredRole?: string }>;
  // Injection for tests
  _cwd?: string;
  devOnly?: boolean;
  mockUsers?: Array<{ name: string; email: string }>;
  nextMajorVersion?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildHierarchy(roles: string[]): Record<string, string[]> {
  const h: Record<string, string[]> = {};
  for (let i = 0; i < roles.length - 1; i++) {
    h[roles[i]!] = roles.slice(i + 1);
  }
  return h;
}

function hierarchyToYaml(hierarchy: Record<string, string[]>, indent: string): string {
  const entries = Object.entries(hierarchy);
  if (entries.length === 0) return `${indent}{}`;
  return entries.map(([role, subs]) => `${indent}${role}: [${subs.join(', ')}]`).join('\n');
}

function rolesToYaml(roles: string[], indent: string): string {
  // rbacConfigSchema expects roles as { name } objects — emit name: prefix
  return roles.map((r) => `${indent}- name: ${r}`).join('\n');
}

// ---------------------------------------------------------------------------
// Per-route RBAC normalization — swp-owu0
// ---------------------------------------------------------------------------

export type NormalizedRoute = { pattern: string; requiredRole: string };

/**
 * Normalize the heterogeneous `protectedRoutes` input into a uniform array.
 * Bare strings fall back to `defaultRole` (backward-compat).
 * Objects use their explicit `requiredRole`, falling back to `defaultRole`.
 */
export function normalizeRoutes(
  routes: Array<string | { pattern: string; requiredRole?: string }>,
  defaultRole: string
): NormalizedRoute[] {
  return routes.map((r) =>
    typeof r === 'string'
      ? { pattern: r, requiredRole: defaultRole }
      : { pattern: r.pattern, requiredRole: r.requiredRole ?? defaultRole }
  );
}

function routesToYaml(routes: NormalizedRoute[], indent: string): string {
  return routes
    .map((r) => `${indent}- pattern: ${r.pattern}\n${indent}  requiredRole: ${r.requiredRole}`)
    .join('\n');
}

/** Auto-detect Next.js major version from package.json in the project root. */
function detectNextMajorVersion(cwd: string): number | null {
  try {
    const pkgPath = join(cwd, 'package.json');
    if (!existsSync(pkgPath)) return null;
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf8'));
    const nextVersion = pkg.dependencies?.next ?? pkg.devDependencies?.next;
    if (!nextVersion) return null;
    // Extract major version from semver-ish strings: "16.2.9", "^16.0.0", ">=16.2.3", etc.
    const match = nextVersion.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Content generators
// ---------------------------------------------------------------------------

function generateMiddlewareContent(
  method: string,
  params: ConfigureAuthParams,
  roles: string[],
  defaultRole: string,
  hierarchy: Record<string, string[]>,
  auditEnabled: boolean,
  auditRetentionDays: number,
  protectedRoutes: NormalizedRoute[]
): string {
  const rbacBlock = `  rbac: {
    roles: ${JSON.stringify(roles)},
    defaultRole: '${defaultRole}',
    hierarchy: ${JSON.stringify(hierarchy, null, 4)},
  },`;
  const auditBlock = `  audit: {
    enabled: ${auditEnabled},
    retentionDays: ${auditRetentionDays},
  },`;
  const patterns = protectedRoutes.map((r) => r.pattern);
  const routesBlock = `  protectedRoutes: ${JSON.stringify(patterns)},`;
  const configBlock = `export const config = {
  matcher: ${JSON.stringify(patterns)},
};`;

  if (method === 'cac') {
    const caBundle = params.cacCaBundle ?? './certs/dod-ca-bundle.pem';
    const edipiLookup = params.cacEdipiLookup ?? './config/edipi-lookup.json';
    const ocspEndpoint = params.cacOcspEndpoint ?? 'https://ocsp.disa.mil';
    const certHeader = params.cacCertHeader ?? 'X-SSL-Client-Cert';
    return `// middleware.ts — generated by @stackwright-pro/auth
// ⚠️  SECURITY REVIEW REQUIRED — CAC/PKI certificate validation
// DoD security officer review required before production deployment.
// Verify: CA bundle completeness, EDIPI lookup endpoint, OCSP accessibility.
import { createProMiddleware } from '@stackwright-pro/auth-nextjs/edge';

export const middleware = createProMiddleware({
  method: 'cac',
  cac: {
    caBundle: process.env.CAC_CA_BUNDLE ?? '${caBundle}',
    edipiLookup: '${edipiLookup}',
    ocspEndpoint: process.env.CAC_OCSP_ENDPOINT ?? '${ocspEndpoint}',
    certHeader: '${certHeader}',
  },
${rbacBlock}
${auditBlock}
${routesBlock}
});

${configBlock}
`;
  }

  if (method === 'oidc') {
    const scopes = params.oidcScopes ?? 'openid profile email';
    const roleClaim = params.oidcRoleClaim ?? 'roles';
    return `// middleware.ts — generated by @stackwright-pro/auth-nextjs
import { createProMiddleware } from '@stackwright-pro/auth-nextjs/edge';

export const middleware = createProMiddleware({
  method: 'oidc',
  oidc: {
    discoveryUrl: process.env.OIDC_DISCOVERY_URL!,
    clientId: process.env.OIDC_CLIENT_ID!,
    clientSecret: process.env.OIDC_CLIENT_SECRET!,
    scopes: '${scopes}',
    roleClaim: '${roleClaim}',
  },
${rbacBlock}
${auditBlock}
${routesBlock}
});

${configBlock}
`;
  }

  // oauth2
  const scopes = params.oauth2Scopes ?? 'read write';
  return `// middleware.ts — generated by @stackwright-pro/auth-nextjs
import { createProMiddleware } from '@stackwright-pro/auth-nextjs/edge';

export const middleware = createProMiddleware({
  method: 'oauth2',
  oauth2: {
    authorizationUrl: process.env.OAUTH2_AUTH_URL!,
    tokenUrl: process.env.OAUTH2_TOKEN_URL!,
    clientId: process.env.OAUTH2_CLIENT_ID!,
    clientSecret: process.env.OAUTH2_CLIENT_SECRET!,
    scopes: '${scopes}',
  },
${rbacBlock}
${auditBlock}
${routesBlock}
});

${configBlock}
`;
}

function generateProxyContent(
  method: string,
  params: ConfigureAuthParams,
  roles: string[],
  defaultRole: string,
  hierarchy: Record<string, string[]>,
  auditEnabled: boolean,
  auditRetentionDays: number,
  protectedRoutes: NormalizedRoute[]
): string {
  const rbacBlock = `  rbac: {
    roles: ${JSON.stringify(roles)},
    defaultRole: '${defaultRole}',
    hierarchy: ${JSON.stringify(hierarchy, null, 4)},
  },`;
  const auditBlock = `  audit: {
    enabled: ${auditEnabled},
    retentionDays: ${auditRetentionDays},
  },`;
  const patterns = protectedRoutes.map((r) => r.pattern);
  const routesBlock = `  protectedRoutes: ${JSON.stringify(patterns)},`;
  const configBlock = `export const config = {
  matcher: ${JSON.stringify(patterns)},
};`;

  if (method === 'cac') {
    const caBundle = params.cacCaBundle ?? './certs/dod-ca-bundle.pem';
    const edipiLookup = params.cacEdipiLookup ?? './config/edipi-lookup.json';
    const ocspEndpoint = params.cacOcspEndpoint ?? 'https://ocsp.disa.mil';
    const certHeader = params.cacCertHeader ?? 'X-SSL-Client-Cert';
    return `// proxy.ts — generated by @stackwright-pro/auth (Next.js >=16)
//   SECURITY REVIEW REQUIRED — CAC/PKI certificate validation
// DoD security officer review required before production deployment.
// Verify: CA bundle completeness, EDIPI lookup endpoint, OCSP accessibility.
import { createProProxy } from '@stackwright-pro/auth-nextjs/edge';

export const proxy = createProProxy({
  method: 'cac',
  cac: {
    caBundle: process.env.CAC_CA_BUNDLE ?? '${caBundle}',
    edipiLookup: '${edipiLookup}',
    ocspEndpoint: process.env.CAC_OCSP_ENDPOINT ?? '${ocspEndpoint}',
    certHeader: '${certHeader}',
  },
${rbacBlock}
${auditBlock}
${routesBlock}
});

${configBlock}
`;
  }

  if (method === 'oidc') {
    const scopes = params.oidcScopes ?? 'openid profile email';
    const roleClaim = params.oidcRoleClaim ?? 'roles';
    return `// proxy.ts — generated by @stackwright-pro/auth (Next.js >=16)
import { createProProxy } from '@stackwright-pro/auth-nextjs/edge';

export const proxy = createProProxy({
  method: 'oidc',
  oidc: {
    discoveryUrl: process.env.OIDC_DISCOVERY_URL!,
    clientId: process.env.OIDC_CLIENT_ID!,
    clientSecret: process.env.OIDC_CLIENT_SECRET!,
    scopes: '${scopes}',
    roleClaim: '${roleClaim}',
  },
${rbacBlock}
${auditBlock}
${routesBlock}
});

${configBlock}
`;
  }

  // oauth2
  const scopes = params.oauth2Scopes ?? 'read write';
  return `// proxy.ts — generated by @stackwright-pro/auth (Next.js >=16)
import { createProProxy } from '@stackwright-pro/auth-nextjs/edge';

export const proxy = createProProxy({
  method: 'oauth2',
  oauth2: {
    authorizationUrl: process.env.OAUTH2_AUTH_URL!,
    tokenUrl: process.env.OAUTH2_TOKEN_URL!,
    clientId: process.env.OAUTH2_CLIENT_ID!,
    clientSecret: process.env.OAUTH2_CLIENT_SECRET!,
    scopes: '${scopes}',
  },
${rbacBlock}
${auditBlock}
${routesBlock}
});

${configBlock}
`;
}

function generateEnvBlock(method: string, params: ConfigureAuthParams): string {
  if (method === 'cac') {
    return `# Authentication (CAC/PKI — DoD)
# ⚠️  SECURITY REVIEW REQUIRED before production deployment
CAC_CA_BUNDLE=./certs/dod-ca-bundle.pem
CAC_OCSP_ENDPOINT=https://ocsp.disa.mil
`;
  }

  if (method === 'oidc') {
    const label = params.provider ?? 'OIDC';
    const discoveryUrl =
      params.oidcDiscoveryUrl ?? 'https://your-provider/.well-known/openid-configuration';
    return `# Authentication (OIDC — ${label})
OIDC_DISCOVERY_URL=${discoveryUrl}
OIDC_CLIENT_ID=your-client-id
OIDC_CLIENT_SECRET=your-client-secret
`;
  }

  // oauth2
  const authUrl = params.oauth2AuthUrl ?? 'https://your-auth-server/authorize';
  const tokenUrl = params.oauth2TokenUrl ?? 'https://your-auth-server/token';
  return `# Authentication (OAuth2)
OAUTH2_AUTH_URL=${authUrl}
OAUTH2_TOKEN_URL=${tokenUrl}
OAUTH2_CLIENT_ID=your-client-id
OAUTH2_CLIENT_SECRET=your-client-secret
`;
}

function generateYamlBlock(
  method: string,
  params: ConfigureAuthParams,
  roles: string[],
  defaultRole: string,
  hierarchy: Record<string, string[]>,
  auditEnabled: boolean,
  auditRetentionDays: number,
  protectedRoutes: NormalizedRoute[],
  useProxy: boolean
): string {
  // Standalone file — no `auth:` top key; rbacConfigSchema.roles expects { name } objects
  const rbacSection = `rbac:
  roles:
${rolesToYaml(roles, '    ')}
  defaultRole: ${defaultRole}
  hierarchy:
${hierarchyToYaml(hierarchy, '    ')}`;

  const auditSection = `audit:
  enabled: ${auditEnabled}
  retentionDays: ${auditRetentionDays}`;

  // Per-route requiredRole — swp-owu0: use each route's own role, not a flat defaultRole
  const routeLines = routesToYaml(protectedRoutes, '  ');

  const providerLine = params.provider ? `provider: ${params.provider}\n` : '';

  if (method === 'cac') {
    const caBundle = params.cacCaBundle ?? './certs/dod-ca-bundle.pem';
    const edipiLookup = params.cacEdipiLookup ?? './config/edipi-lookup.json';
    const ocspEndpoint = params.cacOcspEndpoint ?? 'https://ocsp.disa.mil';
    const certHeader = params.cacCertHeader ?? 'X-SSL-Client-Cert';
    return `# stackwright.auth.yml — generated by Auth Otter
method: cac
${providerLine}${useProxy ? 'proxy' : 'middleware'}: ./${useProxy ? 'proxy' : 'middleware'}.ts
cac:
  caBundle: \${CAC_CA_BUNDLE}
  edipiLookup: ${edipiLookup}
  ocspEndpoint: \${CAC_OCSP_ENDPOINT}
  certHeader: ${certHeader}
${rbacSection}
protectedRoutes:
${routeLines}
${auditSection}
`;
  }

  if (method === 'oidc') {
    const scopes = params.oidcScopes ?? 'openid profile email';
    const roleClaim = params.oidcRoleClaim ?? 'roles';
    return `# stackwright.auth.yml — generated by Auth Otter
method: oidc
${providerLine}${useProxy ? 'proxy' : 'middleware'}: ./${useProxy ? 'proxy' : 'middleware'}.ts
oidc:
  discoveryUrl: \${OIDC_DISCOVERY_URL}
  clientId: \${OIDC_CLIENT_ID}
  clientSecret: \${OIDC_CLIENT_SECRET}
  scopes: ${scopes}
  roleClaim: ${roleClaim}
${rbacSection}
protectedRoutes:
${routeLines}
${auditSection}
`;
  }

  // oauth2
  const scopes = params.oauth2Scopes ?? 'read write';
  return `# stackwright.auth.yml — generated by Auth Otter
method: oauth2
${providerLine}${useProxy ? 'proxy' : 'middleware'}: ./${useProxy ? 'proxy' : 'middleware'}.ts
oauth2:
  authorizationUrl: \${OAUTH2_AUTH_URL}
  tokenUrl: \${OAUTH2_TOKEN_URL}
  clientId: \${OAUTH2_CLIENT_ID}
  clientSecret: \${OAUTH2_CLIENT_SECRET}
  scopes: ${scopes}
${rbacSection}
protectedRoutes:
${routeLines}
${auditSection}
`;
}

// ---------------------------------------------------------------------------
// Dev-only content generators (no env var references — safe for pnpm dev)
// ---------------------------------------------------------------------------

function generateDevOnlyMiddlewareContent(
  method: string,
  params: ConfigureAuthParams,
  roles: string[],
  defaultRole: string,
  hierarchy: Record<string, string[]>,
  auditEnabled: boolean,
  auditRetentionDays: number,
  protectedRoutes: NormalizedRoute[]
): string {
  const rbacBlock = `  rbac: {\n    roles: ${JSON.stringify(roles)},\n    defaultRole: '${defaultRole}',\n    hierarchy: ${JSON.stringify(hierarchy, null, 4)},\n  },`;
  const auditBlock = `  audit: {\n    enabled: ${auditEnabled},\n    retentionDays: ${auditRetentionDays},\n  },`;
  const patterns = protectedRoutes.map((r) => r.pattern);
  const routesBlock = `  protectedRoutes: ${JSON.stringify(patterns)},`;
  const configBlock = `export const config = {\n  matcher: ${JSON.stringify(patterns)},\n};`;
  const devHeader = [
    '// middleware.ts — generated by @stackwright-pro/auth-nextjs (dev-only mock)',
    '//   DEV ONLY — uses mock authentication from lib/mock-auth.ts',
    '// Do NOT deploy to production.',
    "import { createProMiddleware } from '@stackwright-pro/auth-nextjs/edge';",
    "import { mockAuthProvider } from './lib/mock-auth';",
  ].join('\n');

  if (method === 'cac') {
    const caBundle = params.cacCaBundle ?? './certs/dod-ca-bundle.pem';
    const edipiLookup = params.cacEdipiLookup ?? './config/edipi-lookup.json';
    const ocspEndpoint = params.cacOcspEndpoint ?? 'https://ocsp.disa.mil';
    const certHeader = params.cacCertHeader ?? 'X-SSL-Client-Cert';
    return `${devHeader}\n\nexport const middleware = createProMiddleware({\n  method: 'cac',\n  cac: {\n    caBundle: '${caBundle}',\n    edipiLookup: '${edipiLookup}',\n    ocspEndpoint: '${ocspEndpoint}',\n    certHeader: '${certHeader}',\n    provider: mockAuthProvider,\n  },\n${rbacBlock}\n${auditBlock}\n${routesBlock}\n});\n\n${configBlock}\n`;
  }

  if (method === 'oidc') {
    const scopes = params.oidcScopes ?? 'openid profile email';
    const roleClaim = params.oidcRoleClaim ?? 'roles';
    return `${devHeader}\n\nexport const middleware = createProMiddleware({\n  method: 'oidc',\n  oidc: {\n    discoveryUrl: 'https://dev-mock-oidc/.well-known/openid-configuration',\n    clientId: 'dev-mock-client',\n    clientSecret: 'dev-mock-secret',\n    scopes: '${scopes}',\n    roleClaim: '${roleClaim}',\n    provider: mockAuthProvider,\n  },\n${rbacBlock}\n${auditBlock}\n${routesBlock}\n});\n\n${configBlock}\n`;
  }

  // oauth2
  const scopes = params.oauth2Scopes ?? 'read write';
  return `${devHeader}\n\nexport const middleware = createProMiddleware({\n  method: 'oauth2',\n  oauth2: {\n    authorizationUrl: 'https://dev-mock-oauth2/authorize',\n    tokenUrl: 'https://dev-mock-oauth2/token',\n    clientId: 'dev-mock-client',\n    clientSecret: 'dev-mock-secret',\n    scopes: '${scopes}',\n    provider: mockAuthProvider,\n  },\n${rbacBlock}\n${auditBlock}\n${routesBlock}\n});\n\n${configBlock}\n`;
}

function generateDevOnlyProxyContent(
  method: string,
  params: ConfigureAuthParams,
  roles: string[],
  defaultRole: string,
  hierarchy: Record<string, string[]>,
  auditEnabled: boolean,
  auditRetentionDays: number,
  protectedRoutes: NormalizedRoute[]
): string {
  const rbacBlock = `  rbac: {\n    roles: ${JSON.stringify(roles)},\n    defaultRole: '${defaultRole}',\n    hierarchy: ${JSON.stringify(hierarchy, null, 4)},\n  },`;
  const auditBlock = `  audit: {\n    enabled: ${auditEnabled},\n    retentionDays: ${auditRetentionDays},\n  },`;
  const patterns = protectedRoutes.map((r) => r.pattern);
  const routesBlock = `  protectedRoutes: ${JSON.stringify(patterns)},`;
  const configBlock = `export const config = {\n  matcher: ${JSON.stringify(patterns)},\n};`;
  const devHeader = [
    '// proxy.ts -- generated by @stackwright-pro/auth (Next.js >=16, dev-only mock)',
    '//   DEV ONLY -- uses mock authentication from lib/mock-auth.ts',
    '// Do NOT deploy to production.',
    "import { createProProxy } from '@stackwright-pro/auth-nextjs/edge';",
    "import { mockAuthProvider } from './lib/mock-auth';",
  ].join('\n');

  if (method === 'cac') {
    const caBundle = params.cacCaBundle ?? './certs/dod-ca-bundle.pem';
    const edipiLookup = params.cacEdipiLookup ?? './config/edipi-lookup.json';
    const ocspEndpoint = params.cacOcspEndpoint ?? 'https://ocsp.disa.mil';
    const certHeader = params.cacCertHeader ?? 'X-SSL-Client-Cert';
    return `${devHeader}\n\nexport const proxy = createProProxy({\n  method: 'cac',\n  cac: {\n    caBundle: '${caBundle}',\n    edipiLookup: '${edipiLookup}',\n    ocspEndpoint: '${ocspEndpoint}',\n    certHeader: '${certHeader}',\n    provider: mockAuthProvider,\n  },\n${rbacBlock}\n${auditBlock}\n${routesBlock}\n});\n\n${configBlock}\n`;
  }

  if (method === 'oidc') {
    const scopes = params.oidcScopes ?? 'openid profile email';
    const roleClaim = params.oidcRoleClaim ?? 'roles';
    return `${devHeader}\n\nexport const proxy = createProProxy({\n  method: 'oidc',\n  oidc: {\n    discoveryUrl: 'https://dev-mock-oidc/.well-known/openid-configuration',\n    clientId: 'dev-mock-client',\n    clientSecret: 'dev-mock-secret',\n    scopes: '${scopes}',\n    roleClaim: '${roleClaim}',\n    provider: mockAuthProvider,\n  },\n${rbacBlock}\n${auditBlock}\n${routesBlock}\n});\n\n${configBlock}\n`;
  }

  // oauth2
  const scopes = params.oauth2Scopes ?? 'read write';
  return `${devHeader}\n\nexport const proxy = createProProxy({\n  method: 'oauth2',\n  oauth2: {\n    authorizationUrl: 'https://dev-mock-oauth2/authorize',\n    tokenUrl: 'https://dev-mock-oauth2/token',\n    clientId: 'dev-mock-client',\n    clientSecret: 'dev-mock-secret',\n    scopes: '${scopes}',\n    provider: mockAuthProvider,\n  },\n${rbacBlock}\n${auditBlock}\n${routesBlock}\n});\n\n${configBlock}\n`;
}

function generateDevOnlyYamlBlock(
  method: string,
  params: ConfigureAuthParams,
  roles: string[],
  defaultRole: string,
  hierarchy: Record<string, string[]>,
  auditEnabled: boolean,
  auditRetentionDays: number,
  protectedRoutes: NormalizedRoute[],
  useProxy: boolean
): string {
  // Standalone file — no `auth:` top key; rbacConfigSchema.roles expects { name } objects
  const rbacSection = `rbac:\n  roles:\n${rolesToYaml(roles, '    ')}\n  defaultRole: ${defaultRole}\n  hierarchy:\n${hierarchyToYaml(hierarchy, '    ')}`;
  const auditSection = `audit:\n  enabled: ${auditEnabled}\n  retentionDays: ${auditRetentionDays}`;
  // Per-route requiredRole — swp-owu0: each route carries its own role
  const routeLines = protectedRoutes
    .map((r) => `  - pattern: ${r.pattern}\n    requiredRole: ${r.requiredRole}`)
    .join('\n');
  const providerLine = params.provider ? `provider: ${params.provider}\n` : '';

  if (method === 'cac') {
    const caBundle = params.cacCaBundle ?? './certs/dod-ca-bundle.pem';
    const edipiLookup = params.cacEdipiLookup ?? './config/edipi-lookup.json';
    const ocspEndpoint = params.cacOcspEndpoint ?? 'https://ocsp.disa.mil';
    const certHeader = params.cacCertHeader ?? 'X-SSL-Client-Cert';
    return `# stackwright.auth.yml — generated by Auth Otter\nmethod: cac\ndevOnly: true\n${providerLine}${useProxy ? 'proxy' : 'middleware'}: ./${useProxy ? 'proxy' : 'middleware'}.ts\ncac:\n  caBundle: ${caBundle}\n  edipiLookup: ${edipiLookup}\n  ocspEndpoint: ${ocspEndpoint}\n  certHeader: ${certHeader}\n${rbacSection}\nprotectedRoutes:\n${routeLines}\n${auditSection}\n`;
  }

  if (method === 'oidc') {
    const scopes = params.oidcScopes ?? 'openid profile email';
    const roleClaim = params.oidcRoleClaim ?? 'roles';
    return `# stackwright.auth.yml — generated by Auth Otter\nmethod: oidc\ndevOnly: true\n${providerLine}${useProxy ? 'proxy' : 'middleware'}: ./${useProxy ? 'proxy' : 'middleware'}.ts\noidc:\n  discoveryUrl: https://dev-mock-oidc/.well-known/openid-configuration\n  clientId: dev-mock-client\n  clientSecret: dev-mock-secret\n  scopes: ${scopes}\n  roleClaim: ${roleClaim}\n${rbacSection}\nprotectedRoutes:\n${routeLines}\n${auditSection}\n`;
  }

  // oauth2
  const scopes = params.oauth2Scopes ?? 'read write';
  return `# stackwright.auth.yml — generated by Auth Otter\nmethod: oauth2\ndevOnly: true\n${providerLine}${useProxy ? 'proxy' : 'middleware'}: ./${useProxy ? 'proxy' : 'middleware'}.ts\noauth2:\n  authorizationUrl: https://dev-mock-oauth2/authorize\n  tokenUrl: https://dev-mock-oauth2/token\n  clientId: dev-mock-client\n  clientSecret: dev-mock-secret\n  scopes: ${scopes}\n${rbacSection}\nprotectedRoutes:\n${routeLines}\n${auditSection}\n`;
}

// ---------------------------------------------------------------------------
// Dev-only mock-auth generation (deterministic — no LLM dependency)
// ---------------------------------------------------------------------------

/**
 * Derive a dev-convenience key from a role name.
 * Takes the first underscore-separated segment, lowercased.
 * e.g. ESF8_COORDINATOR → esf8, TRIAGE_OFFICER → triage, ADMIN → admin
 */
export function deriveDevKey(roleName: string): string {
  const segment = roleName.split('_')[0];
  return (segment ?? roleName).toLowerCase();
}

function generateMockAuthContent(
  roles: string[],
  mockUsers?: Array<{ name: string; email: string }>
): string {
  const entries: string[] = [];
  const scriptLines: string[] = [];

  for (let i = 0; i < roles.length; i++) {
    const role = roles[i]!;
    const devKey = deriveDevKey(role);
    const persona = mockUsers?.[i];
    const name = persona?.name ?? `Dev ${role}`;
    const email = persona?.email ?? `dev-${devKey}@example.mil`;
    const edipi = String(i + 1).padStart(10, '0');

    entries.push(`  ${devKey}: {
    id: 'mock-${devKey}-${String(i + 1).padStart(3, '0')}',
    name: '${name}',
    email: '${email}',
    roles: ['${role}'],
    edipi: '${edipi}',
  }`);

    scriptLines.push(` *   pnpm dev:${devKey}   — ${name}`);
  }

  return `/**
 * Mock authentication for development mode.
 *
 * DEV_ONLY_MODE — no real auth provider. Select a persona via MOCK_USER env var
 * or use the convenience scripts in package.json:
${scriptLines.join('\n')}
 */

export const MOCK_USERS = {
${entries.join(',\n')}
} as const;

export type MockRole = keyof typeof MOCK_USERS;

export function getMockUser(role?: string) {
  const key = (role ?? process.env.MOCK_USER) as MockRole | undefined;
  if (!key) return null;
  return MOCK_USERS[key] ?? null;
}

export function mockAuthProvider() {
  return getMockUser();
}
`;
}

function updatePackageJsonScripts(
  existingJson: string,
  roles: string[],
  mockUsers?: Array<{ name: string; email: string }>
): string {
  const pkg = JSON.parse(existingJson);
  const scripts: Record<string, string> = pkg.scripts ?? {};

  // Remove stale generic scaffold scripts
  delete scripts['dev:admin'];
  delete scripts['dev:analyst'];
  delete scripts['dev:viewer'];

  // Mock launcher — one Prism process per integration
  scripts['mock'] = 'node scripts/start-mocks.js';

  // Add role-specific dev scripts — each boots mocks + next dev via concurrently
  for (let i = 0; i < roles.length; i++) {
    const devKey = deriveDevKey(roles[i]!);
    scripts[`dev:${devKey}`] =
      `concurrently "pnpm mock" "NEXT_PUBLIC_MOCK_BACKEND=true MOCK_USER=${devKey} next dev"`;
  }

  pkg.scripts = scripts;
  return JSON.stringify(pkg, null, 2) + '\n';
}

// ---------------------------------------------------------------------------
// Exported handler (called directly in tests — no MCP server spin-up needed)
// ---------------------------------------------------------------------------

export async function configureAuthHandler(
  params: ConfigureAuthParams,
  cwd: string
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  const {
    method,
    provider,
    devOnly = false,
    mockUsers,
    rbacRoles = ['SUPER_ADMIN', 'ADMIN', 'ANALYST'],
    auditEnabled = true,
    auditRetentionDays = 90,
    protectedRoutes = ['/dashboard/:path*'],
  } = params;

  const roles = rbacRoles;
  const defaultRole = params.rbacDefaultRole ?? roles[roles.length - 1]!;
  // swp-owu0: normalize once — bare strings fall back to defaultRole, objects use their role
  const normalizedRoutes = normalizeRoutes(protectedRoutes, defaultRole);
  const hierarchy = buildHierarchy(roles);
  const detectedVersion = params.nextMajorVersion ?? detectNextMajorVersion(cwd);
  const useProxy = (detectedVersion ?? 0) >= 16;
  const conventionFile = useProxy ? 'proxy.ts' : 'middleware.ts';

  // ── Short-circuit for method: none (devOnly: false only) ────────────────
  // When devOnly: true, 'none' is auto-upgraded to 'oidc' below (swp-rp9c).
  if (method === 'none' && !devOnly) {
    return {
      content: [
        {
          type: 'text' as const,
          text: JSON.stringify({
            success: true,
            method: 'none',
            provider: null,
            rbacRoles: roles,
            rbacDefaultRole: defaultRole,
            protectedRoutesCount: protectedRoutes.length,
            filesWritten: [],
            securityWarning: null,
            autoUpgradeWarning: null,
          }),
        },
      ],
    };
  }

  // method: 'none' + devOnly: true → auto-upgrade to oidc mock auth (swp-rp9c).
  // Silently producing empty config is the worst failure mode; the otter prompt
  // tells it to use method: 'oidc' directly, but this is a belt-and-suspenders
  // safety net so a future regression doesn't leave stackwright.yml.auth empty.
  const effectiveMethod = method === 'none' ? 'oidc' : method;
  const autoUpgradeWarning =
    method === 'none'
      ? "method: 'none' + devOnly: true was automatically upgraded to method: 'oidc' + devOnly: true. In DEV_ONLY_MODE, pass method: 'oidc' + devOnly: true directly (swp-5tmy)."
      : null;

  const filesWritten: string[] = [];

  try {
    // ── Step 2 — auth convention file (proxy.ts for Next.js >=16, middleware.ts otherwise) ──
    const authFileContent = devOnly
      ? useProxy
        ? generateDevOnlyProxyContent(
            effectiveMethod,
            params,
            roles,
            defaultRole,
            hierarchy,
            auditEnabled,
            auditRetentionDays,
            normalizedRoutes
          )
        : generateDevOnlyMiddlewareContent(
            effectiveMethod,
            params,
            roles,
            defaultRole,
            hierarchy,
            auditEnabled,
            auditRetentionDays,
            normalizedRoutes
          )
      : useProxy
        ? generateProxyContent(
            effectiveMethod,
            params,
            roles,
            defaultRole,
            hierarchy,
            auditEnabled,
            auditRetentionDays,
            normalizedRoutes
          )
        : generateMiddlewareContent(
            effectiveMethod,
            params,
            roles,
            defaultRole,
            hierarchy,
            auditEnabled,
            auditRetentionDays,
            normalizedRoutes
          );
    writeFileSync(join(cwd, conventionFile), authFileContent, 'utf8');
    filesWritten.push(conventionFile);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return {
      content: [
        {
          type: 'text' as const,
          text: JSON.stringify({
            success: false,
            error: `Failed writing ${conventionFile}: ${msg}`,
          }),
        },
      ],
      isError: true,
    };
  }

  if (!devOnly) {
    try {
      // ── Step 3 — .env.example ─────────────────────────────────────────────
      const envBlock = generateEnvBlock(effectiveMethod, params);
      const envPath = join(cwd, '.env.example');
      if (existsSync(envPath)) {
        const existing = readFileSync(envPath, 'utf8');
        writeFileSync(envPath, existing.trimEnd() + '\n\n' + envBlock, 'utf8');
      } else {
        writeFileSync(envPath, envBlock, 'utf8');
      }
      filesWritten.push('.env.example');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify({ success: false, error: `Failed writing .env.example: ${msg}` }),
          },
        ],
        isError: true,
      };
    }
  }

  // ── Step 2b — lib/mock-auth.ts + package.json scripts (devOnly) ──────
  if (devOnly) {
    try {
      const mockAuthDir = join(cwd, 'lib');
      mkdirSync(mockAuthDir, { recursive: true });
      const mockAuthContent = generateMockAuthContent(roles, mockUsers);
      writeFileSync(join(mockAuthDir, 'mock-auth.ts'), mockAuthContent, 'utf8');
      filesWritten.push('lib/mock-auth.ts');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify({
              success: false,
              error: `Failed writing lib/mock-auth.ts: ${msg}`,
            }),
          },
        ],
        isError: true,
      };
    }

    try {
      const pkgPath = join(cwd, 'package.json');
      if (existsSync(pkgPath)) {
        const existingPkg = readFileSync(pkgPath, 'utf8');
        const updatedPkg = updatePackageJsonScripts(existingPkg, roles, mockUsers);
        writeFileSync(pkgPath, updatedPkg, 'utf8');
        filesWritten.push('package.json');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify({
              success: false,
              error: `Failed updating package.json: ${msg}`,
            }),
          },
        ],
        isError: true,
      };
    }
  }

  try {
    // ── Step 4 — stackwright.auth.yml (standalone sibling file, swp-e4y6) ──────
    const authYaml = devOnly
      ? generateDevOnlyYamlBlock(
          effectiveMethod,
          params,
          roles,
          defaultRole,
          hierarchy,
          auditEnabled,
          auditRetentionDays,
          normalizedRoutes,
          useProxy
        )
      : generateYamlBlock(
          effectiveMethod,
          params,
          roles,
          defaultRole,
          hierarchy,
          auditEnabled,
          auditRetentionDays,
          normalizedRoutes,
          useProxy
        );
    // Always write a fresh standalone file — no upsert into stackwright.yml.
    writeFileSync(join(cwd, 'stackwright.auth.yml'), authYaml, 'utf8');
    filesWritten.push('stackwright.auth.yml');
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return {
      content: [
        {
          type: 'text' as const,
          text: JSON.stringify({
            success: false,
            error: `Failed writing stackwright.auth.yml: ${msg}`,
          }),
        },
      ],
      isError: true,
    };
  }

  // ── Step 5 — Return summary ─────────────────────────────────────────────
  const securityWarning =
    effectiveMethod === 'cac'
      ? 'SECURITY REVIEW REQUIRED — CAC certificate chain must be verified before production deployment'
      : null;

  return {
    content: [
      {
        type: 'text' as const,
        text: JSON.stringify({
          success: true,
          method: effectiveMethod,
          provider: provider ?? null,
          rbacRoles: roles,
          rbacDefaultRole: defaultRole,
          protectedRoutesCount: protectedRoutes.length,
          filesWritten,
          convention: useProxy ? 'proxy' : 'middleware',
          nextMajorVersion: params.nextMajorVersion ?? null,
          securityWarning,
          autoUpgradeWarning,
          ...(devOnly
            ? {
                devScripts: {
                  written: filesWritten.includes('package.json'),
                  scripts: filesWritten.includes('package.json')
                    ? {
                        mock: 'node scripts/start-mocks.js',
                        ...Object.fromEntries(
                          roles.map((r) => {
                            const k = deriveDevKey(r);
                            return [
                              `dev:${k}`,
                              `concurrently "pnpm mock" "NEXT_PUBLIC_MOCK_BACKEND=true MOCK_USER=${k} next dev"`,
                            ];
                          })
                        ),
                      }
                    : {},
                  ...(filesWritten.includes('package.json')
                    ? {}
                    : {
                        warning:
                          'package.json not found — dev scripts were not written. Run the auth phase again after scaffolding creates package.json.',
                      }),
                },
              }
            : {}),
        }),
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerAuthTools(server: McpServer): void {
  server.tool(
    'stackwright_pro_configure_auth',
    'Generate authentication middleware and configuration for a Next.js Stackwright application. ' +
      'Writes `middleware.ts` (or `proxy.ts` for Next.js >=16) from a secure template, writes a standalone `stackwright.auth.yml` sibling file, ' +
      'and generates `.env.example` with required environment variables. ' +
      '⚠️ For CAC/PKI: generated `middleware.ts` carries a SECURITY REVIEW REQUIRED comment — ' +
      'certificate chain validation must be verified by a DoD security officer before production deployment. ' +
      'This is the ONLY approved path to generating `middleware.ts`. Never write TypeScript auth files directly.',
    {
      method: z.enum(['cac', 'oidc', 'oauth2', 'none']),
      provider: z.enum(['azure-ad', 'okta', 'ping', 'cognito', 'custom']).optional(),
      // CAC
      cacCaBundle: z.string().optional(),
      cacEdipiLookup: z.string().optional(),
      cacOcspEndpoint: z.string().optional(),
      cacCertHeader: z.string().optional(),
      // OIDC
      oidcDiscoveryUrl: z.string().optional(),
      oidcClientId: z.string().optional(),
      oidcClientSecret: z.string().optional(),
      oidcScopes: z.string().optional(),
      oidcRoleClaim: z.string().optional(),
      // OAuth2
      oauth2AuthUrl: z.string().optional(),
      oauth2TokenUrl: z.string().optional(),
      oauth2ClientId: z.string().optional(),
      oauth2ClientSecret: z.string().optional(),
      oauth2Scopes: z.string().optional(),
      // RBAC
      rbacRoles: jsonCoerce(z.array(z.string()).optional()),
      rbacDefaultRole: z.string().optional(),
      // Audit
      auditEnabled: boolCoerce(z.boolean().optional()),
      auditRetentionDays: numCoerce(z.number().int().positive().optional()),
      // Routes — accepts bare strings (backward-compat) or per-route objects (swp-owu0)
      protectedRoutes: jsonCoerce(
        z
          .array(
            z.union([
              z.string(),
              z.object({ pattern: z.string(), requiredRole: z.string().optional() }),
            ])
          )
          .optional()
      ),
      // Injection for tests
      _cwd: z.string().optional(),
      devOnly: boolCoerce(z.boolean().optional()),
      mockUsers: jsonCoerce(z.array(z.object({ name: z.string(), email: z.string() })).optional()),
      nextMajorVersion: numCoerce(z.number().int().positive().optional()),
    },
    async (params) => {
      const cwd = params._cwd ?? process.cwd();
      return configureAuthHandler(params, cwd);
    }
  );
}
